import { HyDryMessageFilterType } from "@/utils/HyDryEventMessagesFilter";
import { UserCapability } from "./UserCapability";
import { HyDryEventCategory } from "./HyDryEvent";
import { officeAccess, projectManagementAccess, seeAllMessagesAccess } from "@/permissions/UserPermissions";
import User, { isDeviceManager } from "./User";

/**
 * Filters for HyDry event messages.
 */
interface HyDryEventMessageFilters {
  /* eslint-disable-next-line */
  filterAll: any;
  /* eslint-disable-next-line */
  filterNoProject: any;
  filterMyProject: HyDryEventCategory[];
  showMessagesToProjectOwners: boolean;
  showMessagesToProjectSupervisors: boolean;

  erp: HyDryEventCategory[];
}

/**
 * Settings for HyDry messages.
 */
export interface HyDryMessagesSettings {
  messagesEnabled: boolean;
  mailboxEnabled: boolean;
  messageFilters: HyDryEventMessageFilters;

  areDefaultHyDryMessagesSettings?: boolean;
}

/**
 * Creates default settings for HyDry messages.
 * @returns Default HyDry messages settings.
 */
export function createDefaultHyDryMessagesSettings(): HyDryMessagesSettings {
  return {
    messagesEnabled: true,
    mailboxEnabled: false,
    messageFilters: {
      showMessagesToProjectOwners: true,
      showMessagesToProjectSupervisors: true,

      filterAll: {
        [UserCapability.OFFICE]: [
          HyDryEventCategory.DRYING_PROGRESS,
          HyDryEventCategory.ALERT,
        ],
        [UserCapability.PROJECT_MANAGEMENT]: [
          HyDryEventCategory.DRYING_PROGRESS,
          HyDryEventCategory.ALERT,
        ],
      },
      filterNoProject: {
        [UserCapability.OFFICE]: [
          HyDryEventCategory.ALERT,
          HyDryEventCategory.DRYING_PROGRESS,
        ],
        [UserCapability.PROJECT_MANAGEMENT]: [
          HyDryEventCategory.ALERT,
          HyDryEventCategory.DRYING_PROGRESS,
        ],
      },
      filterMyProject: [
        HyDryEventCategory.DRYING_PROGRESS,
        HyDryEventCategory.ALERT,
      ],
      erp: [
        HyDryEventCategory.ORGANISATION,
        HyDryEventCategory.ALERT,
        HyDryEventCategory.DRYING_PROGRESS,
      ],
    },

    areDefaultHyDryMessagesSettings: true,
  };
}

export function calculateVisibleHyDryEventCategories(
  filterType: HyDryMessageFilterType,
  user: User | null,
  hyDryMessageSettings: HyDryMessagesSettings
) {
  let visibleCategories: HyDryEventCategory[] = [];
  const role = user?.role ?? "";

  if (role == "") {
    return new Set<HyDryEventCategory>(visibleCategories);
  }

  const hasOffice = officeAccess(user);
  const hasProjectManagement = projectManagementAccess(user);

  const office = hasOffice ? UserCapability.OFFICE : "";
  const projectManagement = hasProjectManagement ? UserCapability.PROJECT_MANAGEMENT : "";

  switch (filterType) {
    case HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY: {
      const filterNoProject =
        hyDryMessageSettings.messageFilters?.filterNoProject;

      if (filterNoProject) {
        visibleCategories =
          hyDryMessageSettings.messageFilters.filterNoProject[office]
          ?? [];
        visibleCategories = visibleCategories.concat(
          hyDryMessageSettings.messageFilters.filterNoProject[projectManagement]
          ?? []
        );
        visibleCategories = visibleCategories.filter((item, index) => {
          return (visibleCategories.indexOf(item) == index)
        });
      }

      break;
    }
    case HyDryMessageFilterType.ALL_MESSAGES: {
      if (user && seeAllMessagesAccess(user)) {
        //Ires admin should see all messages
        visibleCategories = [
          HyDryEventCategory.ALERT,
          HyDryEventCategory.ORGANISATION,
          HyDryEventCategory.DRYING_PROGRESS,
          HyDryEventCategory.OTHER,
          HyDryEventCategory.IRES,
        ];
      } else {
        const filterAll = hyDryMessageSettings.messageFilters.filterAll;

        if (filterAll) {
          // Special condition for device managers to bypass the capability checks
          if(isDeviceManager(user)) {
            visibleCategories = [
              HyDryEventCategory.ORGANISATION,
              HyDryEventCategory.ALERT,
              HyDryEventCategory.DRYING_PROGRESS,
            ];
          }
          else {
            visibleCategories =
              hyDryMessageSettings.messageFilters.filterAll[office]
              ?? [];
            visibleCategories = visibleCategories.concat(
              hyDryMessageSettings.messageFilters.filterAll[projectManagement]
              ?? []
            );
          }
          
          visibleCategories = visibleCategories.filter((item, index) => {
            return (visibleCategories.indexOf(item) == index)
          });
        }
      }
      break;
    }
    case HyDryMessageFilterType.MY_MESSAGES:
    case HyDryMessageFilterType.MAILBOX:
      visibleCategories =
        hyDryMessageSettings.messageFilters.filterMyProject ?? [];
      break;
  }

  return new Set<HyDryEventCategory>(visibleCategories);
}
